/**
 * @fileoverview Agent definition for chat-with-story-v1.
 *
 * The first **conversational** agent in the codebase. Unlike primitive
 * agents, this one:
 *   - Takes a multi-turn message history as input instead of context macros.
 *   - Uses tool calling (Genkit `ai.defineTool`) to pull story data lazily.
 *   - Streams tokens and tool-call events back to the UI.
 *   - Does not declare an `output: { schema }` — it returns free-form text.
 *
 * For that reason it lives under `src/ai/agents/conversational/` rather
 * than `primitives/`. Registration still flows through `definitions.ts`
 * and `dev.ts` so the Genkit inspector and admin pages can see it.
 *
 * @category misc
 * @type primitive
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas';
import { ChatWithStoryInputSchema, ChatWithStoryOutputSchema } from './schema';
import { prompt } from './prompts';

export const definition: AppAgentDefinition = {
  id: 'chat-with-story-v1',
  kind: 'primitive',
  name: 'Chat with Your Story',
  description:
    'Conversational assistant for brainstorming about your story. Grounds replies in your manuscript via tool calls — does not modify content.',
  // No dedicated "conversational" category yet in AgentCategorySchema; using
  // `misc` to avoid rippling a new enum value through admin UI filters.
  category: 'misc',
  defaultPrompt: prompt,
  inputSchema: ChatWithStoryInputSchema,
  outputSchema: ChatWithStoryOutputSchema,

  recommendedModels: [
    { modelProvider: 'google-ai', modelName: 'gemini-3-flash-preview' },
  ],

  metadata: {
    status: 'experimental',
    type: 'primitive',
    addedIn: '2026-04',
    lastUpdated: '2026-04',
    taxonomyPath: 'conversational.chat',
    usedBy: [],
    usedInComposites: [],
    relatedAgents: [],
  },
};
